import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  HelpCircle,
  MessageSquare,
  Search,
  Send,
  Star,
} from "lucide-react";
import API from "../../../util/API";
import AdminPageShell from "../components/AdminPageShell";
import {
  Badge,
  Card,
  FieldBase,
  PrimaryButton,
  SecondaryButton,
  SelectBase,
  cx,
} from "../B2CManagement/b2cUi";

const TAB_REVIEWS = "reviews";
const TAB_QNA = "qna";

const normalizeText = (value) => String(value ?? "").trim();

const toDateText = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const toUserName = (obj, fallback = "User") => {
  if (!obj || typeof obj !== "object") return fallback;

  const direct = normalizeText(obj?.name);
  if (direct) return direct;

  const fullName = [normalizeText(obj?.firstName), normalizeText(obj?.lastName)]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fullName) return fullName;

  const username = normalizeText(obj?.username);
  if (username) return username;

  return fallback;
};

const renderStars = (rating) => {
  const count = Math.max(0, Math.min(5, Number(rating) || 0));
  return Array.from({ length: 5 }, (_, idx) => (
    <Star
      key={`star-${idx}`}
      className={cx(
        "h-4 w-4",
        idx < Math.round(count) ? "text-amber-400 fill-amber-400" : "text-slate-200"
      )}
    />
  ));
};

const StoreManagement = () => {
  const [activeTab, setActiveTab] = useState(TAB_REVIEWS);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [reviewsSearch, setReviewsSearch] = useState("");
  const [reviewReplyFilter, setReviewReplyFilter] = useState("all");
  const [reviewReplyDrafts, setReviewReplyDrafts] = useState({});
  const [reviewSavingId, setReviewSavingId] = useState("");

  const [qnaList, setQnaList] = useState([]);
  const [qnaLoading, setQnaLoading] = useState(false);
  const [qnaError, setQnaError] = useState("");
  const [qnaSearch, setQnaSearch] = useState("");
  const [qnaStatusFilter, setQnaStatusFilter] = useState("all");
  const [qnaAnswerDrafts, setQnaAnswerDrafts] = useState({});
  const [qnaSavingId, setQnaSavingId] = useState("");

  const loadReviews = async () => {
    setReviewsLoading(true);
    setReviewsError("");
    try {
      const res = await API.get("/reviews/admin/list", {
        params: {
          page: 1,
          limit: 100,
        },
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setReviews(list);
    } catch (error) {
      setReviewsError(
        error?.response?.data?.message || error?.message || "Failed to load reviews"
      );
    } finally {
      setReviewsLoading(false);
    }
  };

  const loadQna = async () => {
    setQnaLoading(true);
    setQnaError("");
    try {
      const res = await API.get("/qna/admin/list", {
        params: {
          page: 1,
          limit: 100,
        },
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setQnaList(list);
    } catch (error) {
      setQnaError(
        error?.response?.data?.message || error?.message || "Failed to load QnA"
      );
    } finally {
      setQnaLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
    loadQna();
  }, []);

  const reviewStats = useMemo(() => {
    const total = reviews.length;
    const replied = reviews.filter((r) => normalizeText(r?.adminReply?.message)).length;
    const avgRating = total
      ? (
          reviews.reduce((sum, item) => sum + (Number(item?.rating) || 0), 0) / total
        ).toFixed(1)
      : "0.0";

    return {
      total,
      replied,
      pendingReply: Math.max(0, total - replied),
      avgRating,
    };
  }, [reviews]);

  const qnaStats = useMemo(() => {
    const total = qnaList.length;
    const answered = qnaList.filter((q) => q?.isAnswered || normalizeText(q?.answer)).length;
    return {
      total,
      answered,
      pending: Math.max(0, total - answered),
    };
  }, [qnaList]);

  const filteredReviews = useMemo(() => {
    const search = reviewsSearch.toLowerCase().trim();

    return (reviews || []).filter((item) => {
      const hasReply = Boolean(normalizeText(item?.adminReply?.message));
      if (reviewReplyFilter === "replied" && !hasReply) return false;
      if (reviewReplyFilter === "pending" && hasReply) return false;

      if (!search) return true;

      const customer = toUserName(item?.user).toLowerCase();
      const title = normalizeText(item?.title).toLowerCase();
      const comment = normalizeText(item?.comment).toLowerCase();
      const productId = normalizeText(item?.product?.productId).toLowerCase();

      return (
        customer.includes(search) ||
        title.includes(search) ||
        comment.includes(search) ||
        productId.includes(search)
      );
    });
  }, [reviews, reviewsSearch, reviewReplyFilter]);

  const filteredQna = useMemo(() => {
    const search = qnaSearch.toLowerCase().trim();

    return (qnaList || []).filter((item) => {
      const answered = Boolean(item?.isAnswered || normalizeText(item?.answer));
      if (qnaStatusFilter === "answered" && !answered) return false;
      if (qnaStatusFilter === "pending" && answered) return false;

      if (!search) return true;

      const question = normalizeText(item?.question).toLowerCase();
      const answer = normalizeText(item?.answer).toLowerCase();
      const productId = normalizeText(item?.productId || item?.product?.productId).toLowerCase();
      const asker = toUserName(item?.user).toLowerCase();

      return (
        question.includes(search) ||
        answer.includes(search) ||
        productId.includes(search) ||
        asker.includes(search)
      );
    });
  }, [qnaList, qnaSearch, qnaStatusFilter]);

  const updateReviewReplyDraft = (reviewId, value) => {
    const id = normalizeText(reviewId);
    if (!id) return;
    setReviewReplyDrafts((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const updateQnaAnswerDraft = (qnaId, value) => {
    const id = normalizeText(qnaId);
    if (!id) return;
    setQnaAnswerDrafts((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const submitReviewReply = async (reviewId) => {
    const id = normalizeText(reviewId);
    const message = normalizeText(reviewReplyDrafts[id]);

    if (!id || !message) return;

    setReviewSavingId(id);
    setReviewsError("");

    try {
      const res = await API.patch(`/reviews/${encodeURIComponent(id)}/reply`, {
        message,
      });
      const updated = res?.data?.data;
      if (updated && updated._id) {
        setReviews((prev) =>
          prev.map((item) =>
            String(item?._id) === String(updated._id) ? updated : item
          )
        );
      }
      setReviewReplyDrafts((prev) => ({ ...prev, [id]: "" }));
    } catch (error) {
      setReviewsError(
        error?.response?.data?.message || error?.message || "Failed to submit reply"
      );
    } finally {
      setReviewSavingId("");
    }
  };

  const submitQnaAnswer = async (qnaId) => {
    const id = normalizeText(qnaId);
    const answer = normalizeText(qnaAnswerDrafts[id]);

    if (!id || !answer) return;

    setQnaSavingId(id);
    setQnaError("");

    try {
      const res = await API.put(`/qna/${encodeURIComponent(id)}/answer`, {
        answer,
      });
      const updated = res?.data?.data;
      if (updated && updated._id) {
        setQnaList((prev) =>
          prev.map((item) =>
            String(item?._id) === String(updated._id) ? updated : item
          )
        );
      }
      setQnaAnswerDrafts((prev) => ({ ...prev, [id]: "" }));
    } catch (error) {
      setQnaError(
        error?.response?.data?.message || error?.message || "Failed to submit answer"
      );
    } finally {
      setQnaSavingId("");
    }
  };

  return (
    <AdminPageShell
      title="Store Reviews & QnA Management"
      description="Monitor customer reviews and product questions, then respond directly from one place."
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="gray">
            <MessageSquare className="h-4 w-4" />
            {reviewStats.total} reviews
          </Badge>
          <Badge tone="green">
            <CheckCircle2 className="h-4 w-4" />
            {reviewStats.replied} replied
          </Badge>
          <Badge tone="yellow">
            <Clock3 className="h-4 w-4" />
            {qnaStats.pending} unanswered questions
          </Badge>
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <SecondaryButton type="button" onClick={loadReviews}>
            Refresh Reviews
          </SecondaryButton>
          <SecondaryButton type="button" onClick={loadQna}>
            Refresh QnA
          </SecondaryButton>
        </div>
      }
    >
      <div className="space-y-6">
        <Card className="p-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveTab(TAB_REVIEWS)}
              className={cx(
                "rounded-2xl px-4 py-2 text-sm font-semibold transition-colors",
                activeTab === TAB_REVIEWS
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              )}
            >
              Reviews Management
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(TAB_QNA)}
              className={cx(
                "rounded-2xl px-4 py-2 text-sm font-semibold transition-colors",
                activeTab === TAB_QNA
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              )}
            >
              QnA Management
            </button>
          </div>
        </Card>

        {activeTab === TAB_REVIEWS ? (
          <>
            <Card className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Search Reviews
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={reviewsSearch}
                      onChange={(e) => setReviewsSearch(e.target.value)}
                      placeholder="Customer, product id, title"
                      className={cx(FieldBase, "pl-10")}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Reply Status
                  </label>
                  <select
                    className={SelectBase}
                    value={reviewReplyFilter}
                    onChange={(e) => setReviewReplyFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="replied">Replied</option>
                    <option value="pending">Pending Reply</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Badge tone="gray" className="h-[42px] w-full justify-center">
                    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                    Avg Rating {reviewStats.avgRating}
                  </Badge>
                </div>
              </div>
            </Card>

            {reviewsError ? (
              <Card className="p-4 border-rose-200 bg-rose-50 text-sm text-rose-700">{reviewsError}</Card>
            ) : null}

            <Card className="overflow-hidden">
              <div className="p-5 border-b border-slate-200">
                <p className="text-sm font-black text-slate-900">Reviews ({filteredReviews.length})</p>
              </div>

              {reviewsLoading ? (
                <div className="p-8 text-center text-sm text-slate-500">Loading reviews...</div>
              ) : filteredReviews.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No reviews found.</div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {filteredReviews.map((item) => {
                    const id = normalizeText(item?._id);
                    const draft = reviewReplyDrafts[id] ?? "";
                    const existingReply = normalizeText(item?.adminReply?.message);
                    const isSaving = reviewSavingId === id;

                    return (
                      <div key={id} className="p-5 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {toUserName(item?.user)}
                            </p>
                            <p className="text-xs text-slate-500">
                              Product: {normalizeText(item?.product?.productId) || "-"} 
                              {" • "}
                              {toDateText(item?.createdAt)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">{renderStars(item?.rating)}</div>
                        </div>

                        {normalizeText(item?.title) ? (
                          <p className="text-sm font-black text-slate-900">{item.title}</p>
                        ) : null}
                        <p className="text-sm text-slate-700">{normalizeText(item?.comment) || "-"}</p>

                        {existingReply ? (
                          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                            <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
                              Admin Reply
                            </p>
                            <p className="mt-1 text-sm text-emerald-900">{existingReply}</p>
                            <p className="mt-1 text-xs text-emerald-700">
                              {toUserName(item?.adminReply?.repliedBy, "Admin")}
                              {" • "}
                              {toDateText(item?.adminReply?.repliedAt)}
                            </p>
                          </div>
                        ) : null}

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <textarea
                            rows={3}
                            value={draft}
                            onChange={(e) => updateReviewReplyDraft(id, e.target.value)}
                            placeholder="Write admin reply..."
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                          />
                          <div className="mt-3 flex justify-end">
                            <PrimaryButton
                              type="button"
                              disabled={!normalizeText(draft) || isSaving}
                              onClick={() => submitReviewReply(id)}
                            >
                              <Send className="h-4 w-4" />
                              {isSaving ? "Sending..." : existingReply ? "Update Reply" : "Send Reply"}
                            </PrimaryButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </>
        ) : (
          <>
            <Card className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Search QnA
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={qnaSearch}
                      onChange={(e) => setQnaSearch(e.target.value)}
                      placeholder="Question, product id, asker"
                      className={cx(FieldBase, "pl-10")}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Answer Status
                  </label>
                  <select
                    className={SelectBase}
                    value={qnaStatusFilter}
                    onChange={(e) => setQnaStatusFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="answered">Answered</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Badge tone="gray" className="h-[42px] w-full justify-center">
                    <HelpCircle className="h-4 w-4" />
                    {qnaStats.total} total • {qnaStats.answered} answered
                  </Badge>
                </div>
              </div>
            </Card>

            {qnaError ? (
              <Card className="p-4 border-rose-200 bg-rose-50 text-sm text-rose-700">{qnaError}</Card>
            ) : null}

            <Card className="overflow-hidden">
              <div className="p-5 border-b border-slate-200">
                <p className="text-sm font-black text-slate-900">QnA ({filteredQna.length})</p>
              </div>

              {qnaLoading ? (
                <div className="p-8 text-center text-sm text-slate-500">Loading QnA...</div>
              ) : filteredQna.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No questions found.</div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {filteredQna.map((item) => {
                    const id = normalizeText(item?._id);
                    const draft = qnaAnswerDrafts[id] ?? "";
                    const existingAnswer = normalizeText(item?.answer);
                    const isSaving = qnaSavingId === id;

                    return (
                      <div key={id} className="p-5 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              Asked by {toUserName(item?.user)}
                            </p>
                            <p className="text-xs text-slate-500">
                              Product: {normalizeText(item?.productId || item?.product?.productId) || "-"}
                              {" • "}
                              {toDateText(item?.createdAt)}
                            </p>
                          </div>
                          {existingAnswer ? (
                            <Badge tone="green">
                              <CheckCircle2 className="h-4 w-4" />
                              Answered
                            </Badge>
                          ) : (
                            <Badge tone="yellow">
                              <Clock3 className="h-4 w-4" />
                              Pending
                            </Badge>
                          )}
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Question</p>
                          <p className="mt-1 text-sm text-slate-900">{normalizeText(item?.question) || "-"}</p>
                        </div>

                        {existingAnswer ? (
                          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                            <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">Current Answer</p>
                            <p className="mt-1 text-sm text-emerald-900">{existingAnswer}</p>
                            <p className="mt-1 text-xs text-emerald-700">
                              {toUserName(item?.answeredBy, "Admin")}
                            </p>
                          </div>
                        ) : null}

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <textarea
                            rows={3}
                            value={draft}
                            onChange={(e) => updateQnaAnswerDraft(id, e.target.value)}
                            placeholder="Write answer..."
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                          />
                          <div className="mt-3 flex justify-end">
                            <PrimaryButton
                              type="button"
                              disabled={!normalizeText(draft) || isSaving}
                              onClick={() => submitQnaAnswer(id)}
                            >
                              <Send className="h-4 w-4" />
                              {isSaving ? "Sending..." : existingAnswer ? "Update Answer" : "Send Answer"}
                            </PrimaryButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </AdminPageShell>
  );
};

export default StoreManagement;

